import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { projects } from "@/data/projects";
import { responsiveImg } from "@/data/imageWidths";
import { MaskedLine } from "@/components/MaskedLine";
import { Reveal } from "@/components/Reveal";

const CARD_SIZES = "(min-width: 1440px) 793px, (min-width: 768px) 58vw, 100vw";

const Hero = () => {
  return (
    <section data-testid="hero">
      <div className="mx-auto max-w-[1440px] px-5 md:px-10 pt-28 md:pt-40">
        <div className="flex justify-between font-mono text-[10px] md:text-xs tracking-[0.25em] text-[#6E6B65]">
          <MaskedLine delay={0.05}>
            <span data-testid="hero-kicker-left">PORTFOLIO — ©2026</span>
          </MaskedLine>
          <MaskedLine delay={0.15}>
            <span data-testid="hero-kicker-right">RENO, NEVADA</span>
          </MaskedLine>
        </div>

        <h1
          className="mt-10 md:mt-16 font-serif font-medium tracking-[-0.03em] leading-[0.92] text-[#1A1918] text-[16vw] md:text-[11.5vw]"
          data-testid="hero-heading"
        >
          <MaskedLine delay={0.2}>Christopher</MaskedLine>
          <MaskedLine delay={0.34}>
            <span className="italic font-light">
              Collins<span className="text-[#C85A32] not-italic">.</span>
            </span>
          </MaskedLine>
        </h1>

        <div className="mt-12 md:mt-16 grid md:grid-cols-12 gap-8 items-end">
          <div className="md:col-span-6">
            <MaskedLine delay={0.5}>
              <p
                className="font-mono text-[10px] md:text-xs tracking-[0.25em] text-[#6E6B65]"
                data-testid="hero-role"
              >
                PRODUCT / UX / BRAND DESIGNER
              </p>
            </MaskedLine>
            <Reveal delay={0.6}>
              <p
                className="mt-6 text-lg md:text-xl leading-relaxed text-[#3A3835] max-w-md"
                data-testid="hero-intro"
              >
                I design digital products, brands, and experiences that connect
                culture, utility, and people.
              </p>
            </Reveal>
          </div>
          <div className="md:col-span-6 flex md:justify-end">
            <Reveal delay={0.7}>
              <button
                onClick={() =>
                  window.__lenis?.scrollTo("#work", { offset: -64 })
                }
                data-testid="hero-scroll-cue"
                aria-label="Scroll to selected work"
                className="group flex items-center gap-4 font-mono text-[10px] md:text-xs tracking-[0.25em] text-[#6E6B65] hover:text-[#1A1918] transition-colors duration-300"
              >
                SELECTED WORK
                <span className="flex items-center justify-center w-11 h-11 rounded-full border border-[#C9C2B6] transition-colors duration-300 group-hover:bg-[#1A1918] group-hover:border-[#1A1918] group-hover:text-[#FBF9F5]">
                  <ArrowDown
                    size={16}
                    className="transition-transform duration-500 group-hover:translate-y-0.5"
                  />
                </span>
              </button>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};

const ProjectRow = ({ project, flip }) => (
  <Reveal>
    <article
      className="border-t border-[#D8D2C7] py-14 md:py-20 grid md:grid-cols-12 gap-10"
      data-testid={`project-card-${project.id}`}
    >
      <div
        className={`md:col-span-5 flex flex-col justify-between ${
          flip ? "md:order-2" : ""
        }`}
      >
        <div>
          <div className="flex items-baseline gap-4">
            <span className="font-mono text-xs text-[#C85A32]">
              {project.index}
            </span>
            <h3 className="font-serif text-4xl md:text-5xl tracking-[-0.02em] text-[#1A1918]">
              {project.title}
            </h3>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 font-mono text-[10px] md:text-xs tracking-[0.25em] text-[#6E6B65]">
            <span>{project.category.toUpperCase()}</span>
            {project.status === "IN DEVELOPMENT" && (
              <span
                className="border border-[#234259] text-[#234259] px-2.5 py-1"
                data-testid="badge-in-development"
              >
                IN DEVELOPMENT
              </span>
            )}
          </div>
          <p className="mt-6 text-base md:text-lg leading-relaxed text-[#3A3835] max-w-md">
            {project.blurb}
          </p>
        </div>
        <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3 font-sans tracking-[0.18em] text-[11px] md:text-xs text-[#1A1918]">
          <Link
            to={`/work/${project.id}`}
            data-testid={`view-case-study-${project.id}`}
            className="link-sweep inline-flex items-center gap-2 py-1"
          >
            {project.linkLabel || "VIEW CASE STUDY"} <ArrowUpRight size={14} />
          </Link>
          {project.links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              target="_blank"
              rel="noreferrer"
              data-testid={`visit-${project.id}`}
              className="link-sweep inline-flex items-center gap-2 py-1 text-[#6E6B65] hover:text-[#1A1918] transition-colors duration-300"
            >
              {l.label} <ArrowUpRight size={14} />
            </a>
          ))}
        </div>
      </div>
      <Link
        to={`/work/${project.id}`}
        className={`md:col-span-7 group block ${flip ? "md:order-1" : ""}`}
        data-testid={`project-image-${project.id}`}
      >
        <div className="overflow-hidden bg-[#E8E4DC]">
          <img
            {...responsiveImg(project.cardImage || project.heroImage, CARD_SIZES)}
            alt={`${project.title} project imagery`}
            loading="lazy"
            decoding="async"
            className="w-full aspect-[4/3] object-cover transition-transform duration-[1400ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.03]"
          />
        </div>
        <p className="mt-3 font-mono text-[10px] tracking-[0.25em] text-[#99958D]">
          {project.cardCaption || project.frameCaption}
        </p>
      </Link>
    </article>
  </Reveal>
);

export default function Home() {
  const { hash } = useLocation();

  useEffect(() => {
    if (hash === "#work") {
      const t = setTimeout(
        () => window.__lenis?.scrollTo("#work", { offset: -64 }),
        200
      );
      return () => clearTimeout(t);
    }
  }, [hash]);

  return (
    <main data-testid="home-page">
      <Hero />
      <section
        id="work"
        className="mx-auto max-w-[1440px] px-5 md:px-10 mt-20 md:mt-32"
        data-testid="work-section"
      >
        <Reveal className="flex items-baseline justify-between pb-10 md:pb-14">
          <h2 className="font-serif text-3xl md:text-5xl tracking-[-0.02em] text-[#1A1918]">
            Selected Work
          </h2>
          <span className="font-mono text-[10px] md:text-xs tracking-[0.25em] text-[#6E6B65]">
            FIVE PROJECTS
          </span>
        </Reveal>
        {projects.map((p, i) => (
          <ProjectRow key={p.id} project={p} flip={i % 2 === 1} />
        ))}
      </section>
    </main>
  );
}
